import { ImpressionsList, Image } from '../types/types';
import { validateAsset } from '@dertour/common-lib';

interface FetchImpressionsParams {
    brand: string;
    tenant: string;
    currency: string;
    lang: string;
    identifier: string;
    intent?: string;
    dataSrc?: string;
}

interface ApiResponse {
    title?: string;
    intro?: string;
    items?: Array<{
        id?: string;
        title: string;
        text: string;
        images?: Image[];
    }>;
    meta?: {
        service: string;
        version: string;
        buildTime: string;
    };
    error?: string;
    missing?: string[];
    invalid?: string[];
}

/**
 * Service for loading impressions data via midlayer
 */
export class ImpressionsService {
    /**
     * Fetch impressions data from midlayer Azure Function
     * @param params - Request parameters including brand, tenant, currency, lang, identifier, intent, travelContext
     * @returns Promise with ImpressionsList
     */
    static async fetchImpressions(params: FetchImpressionsParams): Promise<ImpressionsList> {
        const { brand, tenant, currency, lang, identifier, intent } = params;
        const url = new URL(import.meta.env.VITE_SERVICE_URL);
        url.searchParams.set('brand', brand);
        url.searchParams.set('tenant', tenant);
        url.searchParams.set('currency', currency);
        url.searchParams.set('lang', lang);
        url.searchParams.set('identifier', identifier);
        url.searchParams.set('travelcontext', 'ROUNDTRIPS');
        if (intent) {
            url.searchParams.set('intent', intent);
        }
        // Note: imagesize parameter is not needed - backend always returns all image sizes

        try {
            const response = await fetch(url.toString());

            if (!response.ok) {
                // Try to parse error response
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData: ApiResponse = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                        if (errorData.missing && errorData.missing.length > 0) {
                            errorMessage += ` (Missing parameters: ${errorData.missing.join(', ')})`;
                        }
                        if (errorData.invalid && errorData.invalid.length > 0) {
                            errorMessage += ` (Invalid parameters: ${errorData.invalid.join(', ')})`;
                        }
                    }
                } catch {
                    // Ignore JSON parse errors for error response
                }
                throw new Error(errorMessage);
            }

            const data: ApiResponse = await response.json();

            // Validate data
            if (!data.items || data.items.length === 0) {
                throw new Error('impressionsList is missing or impressionsList.items is empty.');
            }

            // Warn if there are 2 or fewer items (component requires at least 3)
            if (data.items.length <= 2) {
                console.warn(
                    `[ImpressionsService] Component requires at least 3 items to display. Found ${data.items.length} item(s).`
                );
            }

            // Validate images - be lenient with failures
            // Items without images are filtered out
            const validationResults = await Promise.all(
                data.items.map(async (item, index) => {
                    // Only validate if images array exists and has items
                    if (!item.images || item.images.length === 0) {
                        return false; // Filter out items without images
                    }
                    // Prefer MOBILE for validation (smaller, faster to validate)
                    const mobileImage = item.images.find((img) => img.size === 'MOBILE');
                    const imageUrl = mobileImage ? mobileImage.url : item.images[0].url;
                    try {
                        // Only preload first image
                        return await validateAsset(imageUrl, index === 0);
                    } catch {
                        return false;
                    }
                })
            );
            const validatedItems = data.items
                .map((item) => {
                    // Generate img fallback from images array - use MOBILE as fallback for better performance
                    // The browser will automatically select the optimal size from srcset (with width descriptors)
                    // If srcset fails or isn't supported, MOBILE is smaller than ORIGINAL, saving bandwidth
                    let imgUrl = '';
                    if (item.images && item.images.length > 0) {
                        // Prefer MOBILE as fallback for better performance
                        // Browser will use srcset to select optimal size, src is only fallback
                        const mobileImage = item.images.find((img) => img.size === 'MOBILE');
                        imgUrl = mobileImage ? mobileImage.url : item.images[0].url;
                    }

                    return {
                        ...item,
                        img: imgUrl, // Always set img from images array
                        images: item.images || undefined,
                    };
                })
                .filter((_, index) => Boolean(validationResults[index]));

            return {
                title: data.title || '',
                intro: data.intro || '',
                items: validatedItems,
            };
        } catch (error) {
            console.error('❌ [ImpressionsService] Error:', error);
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('Failed to fetch impressions data', { cause: error });
        }
    }
}
